import { useState, type ReactNode } from 'react';
import { Eye, EyeOff } from '../icons';

interface PasswordInputProps {
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  autoComplete?: 'new-password' | 'current-password';
  showStrength?: boolean;
  label?: ReactNode;
}

type Level = '弱' | '中' | '强';

function scorePassword(pw: string): { score: number; level: Level } {
  let score = 0;
  if (pw.length >= 6) score += 1;
  if (pw.length >= 10) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  score = Math.min(5, Math.max(0, score));
  const level: Level = score <= 2 ? '弱' : score <= 4 ? '中' : '强';
  return { score, level };
}

const SEGMENT_COLOR: Record<Level, string> = {
  弱: 'bg-red-400',
  中: 'bg-amber-400',
  强: 'bg-emerald-500',
};

const LEVEL_SEGMENTS: Record<Level, number> = { 弱: 1, 中: 2, 强: 3 };

const LEVEL_TEXT_COLOR: Record<Level, string> = {
  弱: 'text-danger',
  中: 'text-warn',
  强: 'text-success',
};

export function PasswordInput({
  value,
  onChange,
  required,
  autoComplete = 'current-password',
  showStrength = false,
  label = '密码',
}: PasswordInputProps) {
  const [visible, setVisible] = useState(false);
  const { level } = scorePassword(value);
  const lit = LEVEL_SEGMENTS[level];

  return (
    <div>
      <label className="block text-sm font-medium text-fg-muted">{label}</label>
      <div className="relative mt-1">
        <input
          type={visible ? 'text' : 'password'}
          required={required}
          autoComplete={autoComplete}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded border border-line px-3 py-2 pr-10 text-sm focus:border-navy focus:outline-none"
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          className="absolute inset-y-0 right-2 flex items-center px-1 text-fg-subtle hover:text-fg"
          aria-label={visible ? '隐藏密码' : '显示密码'}
          tabIndex={-1}
        >
          {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
        </button>
      </div>
      {showStrength && value.length > 0 && (
        <div className="mt-2 flex items-center gap-2">
          <div className="flex flex-1 gap-1">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className={`h-1 flex-1 rounded ${i < lit ? SEGMENT_COLOR[level] : 'bg-surface-muted'}`}
              />
            ))}
          </div>
          <span className={`text-xs ${LEVEL_TEXT_COLOR[level]}`}>密码强度：{level}</span>
        </div>
      )}
    </div>
  );
}
